import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import DiyLayout from "../../components/Layout/DiyLayout";
import { useDiySelfClient } from "../../hooks/useDiySelfClient";
import { disputesApi, type UpdateDisputeRequest } from "../../api/disputesApi";
import { disputeLettersApi } from "../../api/disputeLettersApi";
import type { DisputeItem, DisputeLetter } from "../../types";
import {
  FALLBACK_LETTER_TEMPLATES,
  LETTER_BUREAU_TARGETS,
  modalInputClass,
} from "../../components/diy/diyModalConstants";
import type { LetterTemplate } from "../../types";
import { TableSkeletonRows } from "../../components/ui/Skeleton";
import { isoToDatetimeLocalValue, datetimeLocalValueToIso } from "../../components/diy/diyModalConstants";
import { ScrollReveal } from "../../components/diy/ScrollReveal";
import "../../styles/diy-animations.css";

function printLetter(title: string, body: string) {
  const w = window.open("", "_blank", "width=800,height=900");
  if (!w) {
    return;
  }
  w.document.write(
    `<!DOCTYPE html><html><head><title>${title}</title><style>body{font-family:system-ui;padding:24px;color:#111;line-height:1.5;white-space:pre-wrap;}</style></head><body><h1>${title}</h1><div>${body.replace(/</g, "&lt;")}</div></body></html>`
  );
  w.document.close();
  w.focus();
  w.print();
}

function downloadLetterFile(title: string, body: string) {
  const safe = title.replace(/[^\w-]+/g, "_").slice(0, 80);
  const blob = new Blob([body], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safe || "letter"}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

const btnSecondary =
  "cursor-pointer rounded-xl border px-3 py-2 text-xs font-semibold transition-colors duration-200";
const btnSecondaryStyle: React.CSSProperties = {
  fontFamily: "'DM Sans', sans-serif",
  borderColor: "rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.06)",
  color: "#E2E8F0",
};

const glass = {
  background: "rgba(255, 255, 255, 0.03)",
  border: "1px solid rgba(255, 255, 255, 0.07)",
};

const DiyMyLettersPage: React.FC = () => {
  const { clientId, loading: clientLoading, error: clientError, reload: reloadClient } = useDiySelfClient();
  const [disputes, setDisputes] = useState<DisputeItem[]>([]);
  const [lettersByDispute, setLettersByDispute] = useState<Record<string, DisputeLetter[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [templates, setTemplates] = useState<LetterTemplate[]>([]);
  const [guideOpen, setGuideOpen] = useState(true);
  const [genState, setGenState] = useState<
    Record<string, { letterType: string; bureau: string; body: string; busy: boolean; err: string }>
  >({});
  const [markDates, setMarkDates] = useState<Record<string, string>>({});

  const letterOptions = useMemo(
    () => (templates.length > 0 ? templates : FALLBACK_LETTER_TEMPLATES),
    [templates]
  );

  const load = useCallback(async () => {
    if (!clientId) {
      setDisputes([]);
      setLettersByDispute({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const list = await disputesApi.getAllForClient(clientId);
      setDisputes(list);
      const entries = await Promise.all(
        list.map(async (d) => {
          const letters = await disputeLettersApi.getLettersForDispute(d.id).catch(() => [] as DisputeLetter[]);
          return [d.id, letters] as const;
        })
      );
      setLettersByDispute(Object.fromEntries(entries));
    } catch {
      setError("Could not load letters.");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void disputeLettersApi.getTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, []);

  const disputesWithoutLetters = useMemo(
    () => disputes.filter((d) => (lettersByDispute[d.id]?.length ?? 0) === 0),
    [disputes, lettersByDispute]
  );

  const creditorByDisputeId = useMemo(() => {
    const m: Record<string, string> = {};
    for (const d of disputes) {
      m[d.id] = d.creditor;
    }
    return m;
  }, [disputes]);

  const ensureGen = (disputeId: string) => {
    setGenState((prev) => {
      if (prev[disputeId]) {
        return prev;
      }
      return {
        ...prev,
        [disputeId]: {
          letterType: letterOptions[0]?.typeKey ?? "Validation",
          bureau: LETTER_BUREAU_TARGETS[0],
          body: "",
          busy: false,
          err: "",
        },
      };
    });
  };

  const generate = async (disputeId: string, letterType: string, bureau: string) => {
    ensureGen(disputeId);
    setGenState((prev) => {
      const g = prev[disputeId] ?? {
        letterType,
        bureau,
        body: "",
        busy: false,
        err: "",
      };
      return { ...prev, [disputeId]: { ...g, letterType, bureau, busy: true, err: "" } };
    });
    try {
      const text = await disputeLettersApi.generateLetter(disputeId, {
        letterType,
        bureauTarget: bureau,
      });
      setGenState((prev) => {
        const g = prev[disputeId] ?? {
          letterType,
          bureau,
          body: "",
          busy: false,
          err: "",
        };
        return { ...prev, [disputeId]: { ...g, body: text, busy: false } };
      });
    } catch {
      setGenState((prev) => {
        const g = prev[disputeId] ?? {
          letterType,
          bureau,
          body: "",
          busy: false,
          err: "",
        };
        return { ...prev, [disputeId]: { ...g, busy: false, err: "Generation failed." } };
      });
    }
  };

  function buildUpdatePayload(dispute: DisputeItem, lastLetterSentAt: string | null): UpdateDisputeRequest {
    const now = new Date().toISOString();
    return {
      creditor: dispute.creditor.trim(),
      accountType: dispute.accountType,
      accountNumber: dispute.accountNumber.trim(),
      balance: dispute.balance,
      status: dispute.status,
      disputeReason: dispute.disputeReason.trim(),
      disputeStrategy: dispute.disputeStrategy,
      letterType: dispute.letterType.trim(),
      priorityRank: dispute.priorityRank,
      estimatedScoreImpact: dispute.estimatedScoreImpact,
      equifaxDisputed: dispute.equifaxDisputed,
      experianDisputed: dispute.experianDisputed,
      transUnionDisputed: dispute.transUnionDisputed,
      roundNumber: dispute.roundNumber,
      statuteOfLimitationsFlag: dispute.statuteOfLimitationsFlag,
      notes: dispute.notes.trim(),
      lastLetterSentAt,
      responseReceivedAt: dispute.responseReceivedAt ?? null,
      updatedAt: now,
    };
  }

  const saveDraft = async (disputeId: string) => {
    const g = genState[disputeId];
    if (!g?.body.trim()) {
      return;
    }
    const name = letterOptions.find((t) => t.typeKey === g.letterType)?.name ?? g.letterType;
    await disputeLettersApi.saveLetter(disputeId, {
      letterContent: g.body,
      letterType: name,
      bureauTarget: g.bureau,
      markAsSent: false,
    });
    await load();
  };

  const saveSent = async (disputeId: string) => {
    const g = genState[disputeId];
    if (!g?.body.trim()) {
      return;
    }
    const name = letterOptions.find((t) => t.typeKey === g.letterType)?.name ?? g.letterType;
    const local =
      markDates[`new-${disputeId}`] ?? isoToDatetimeLocalValue(new Date().toISOString());
    const iso = datetimeLocalValueToIso(local);
    await disputeLettersApi.saveLetter(disputeId, {
      letterContent: g.body,
      letterType: name,
      bureauTarget: g.bureau,
      markAsSent: true,
    });
    const d = disputes.find((x) => x.id === disputeId);
    if (d && iso) {
      try {
        await disputesApi.update(d.clientId, disputeId, buildUpdatePayload(d, iso));
      } catch {
        /* letter saved even if dispute patch fails */
      }
    }
    await load();
  };

  const markExistingSent = async (letter: DisputeLetter) => {
    const dispute = disputes.find((d) => d.id === letter.disputeItemId);
    if (!dispute) {
      return;
    }
    const local = markDates[letter.id] || isoToDatetimeLocalValue(new Date().toISOString());
    const iso = datetimeLocalValueToIso(local);
    await disputeLettersApi.saveLetter(letter.disputeItemId, {
      letterContent: letter.letterContent,
      letterType: letter.letterType,
      bureauTarget: letter.bureauTarget,
      markAsSent: true,
    });
    if (iso) {
      await disputesApi.update(dispute.clientId, letter.disputeItemId, buildUpdatePayload(dispute, iso));
    }
    await load();
  };

  const allLetters = useMemo(() => {
    const flat: DisputeLetter[] = [];
    for (const arr of Object.values(lettersByDispute)) {
      flat.push(...arr);
    }
    return flat.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [lettersByDispute]);

  return (
    <DiyLayout>
      <div className="mx-auto max-w-lg space-y-8 md:max-w-2xl">
        <header>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 22, color: "#F1F5F9" }}>My Letters</h1>
          <p className="mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#94A3B8" }}>
            AI-generated dispute letters ready to send
          </p>
        </header>

        {clientError ? (
          <p className="text-sm" style={{ color: "#FECACA", fontFamily: "'DM Sans', sans-serif" }}>
            {clientError}{" "}
            <button type="button" className="underline" style={{ color: "#0EA5E9" }} onClick={() => void reloadClient()}>
              Retry
            </button>
          </p>
        ) : null}

        {loading || clientLoading ? (
          <TableSkeletonRows rows={4} cols={2} />
        ) : error ? (
          <p className="text-sm" style={{ color: "#FECACA", fontFamily: "'DM Sans', sans-serif" }}>
            {error}{" "}
            <button type="button" className="underline" style={{ color: "#0EA5E9" }} onClick={() => void load()}>
              Retry
            </button>
          </p>
        ) : disputes.length === 0 ? (
          <ScrollReveal>
            <div className="rounded-2xl px-6 py-12 text-center" style={glass}>
              <h2 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 18, color: "#F1F5F9" }}>
                No letters generated yet
              </h2>
              <p className="mt-2" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#94A3B8" }}>
                Create disputes first, then generate letters.
              </p>
              <Link
                to="/diy/my-disputes"
                className="mt-6 inline-block rounded-xl px-6 py-3 text-center font-semibold text-white no-underline transition-transform hover:scale-[1.02]"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                  boxShadow: "0 4px 15px rgba(16,185,129,0.3)",
                }}
              >
                Go to disputes
              </Link>
            </div>
          </ScrollReveal>
        ) : (
          <>
            <section className="space-y-4">
              <h2 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 16, color: "#F1F5F9" }}>
                Generate letters
              </h2>
              {disputesWithoutLetters.length === 0 ? (
                <p className="text-sm text-slate-500">Every dispute has at least one letter — or add a new dispute.</p>
              ) : (
                <div className="space-y-4">
                  {disputesWithoutLetters.map((d) => {
                    ensureGen(d.id);
                    const g =
                      genState[d.id] ??
                      ({
                        letterType: letterOptions[0]?.typeKey ?? "",
                        bureau: LETTER_BUREAU_TARGETS[0],
                        body: "",
                        busy: false,
                        err: "",
                      } as const);
                    return (
                      <div key={d.id} className="card-hover rounded-xl p-4" style={glass}>
                        <p style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 14, color: "#F1F5F9" }}>{d.creditor}</p>
                        <p className="text-xs" style={{ fontFamily: "'DM Sans', sans-serif", color: "#64748B" }}>
                          No letters yet for this dispute.
                        </p>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          <label className="block text-xs text-slate-500">
                            Letter type
                            <select
                              className={modalInputClass}
                              value={g.letterType}
                              onChange={(e) =>
                                setGenState((prev) => ({
                                  ...prev,
                                  [d.id]: { ...g, letterType: e.target.value },
                                }))
                              }
                            >
                              {letterOptions.map((t) => (
                                <option key={t.typeKey} value={t.typeKey}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="block text-xs text-slate-500">
                            Bureau
                            <select
                              className={modalInputClass}
                              value={g.bureau}
                              onChange={(e) =>
                                setGenState((prev) => ({
                                  ...prev,
                                  [d.id]: { ...g, bureau: e.target.value },
                                }))
                              }
                            >
                              {LETTER_BUREAU_TARGETS.map((b) => (
                                <option key={b} value={b}>
                                  {b}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                        <button
                          type="button"
                          onClick={() => void generate(d.id, g.letterType, g.bureau)}
                          disabled={g.busy}
                          className="mt-3 cursor-pointer rounded-xl border-0 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
                          style={{
                            fontFamily: "'DM Sans', sans-serif",
                            background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                            boxShadow: "0 4px 15px rgba(16,185,129,0.25)",
                          }}
                        >
                          {g.busy ? "Generating…" : "Generate Letter"}
                        </button>
                        {g.err ? <p className="mt-2 text-sm" style={{ color: "#FECACA" }}>{g.err}</p> : null}
                        {g.body ? (
                          <>
                            <textarea
                              className={`${modalInputClass} mt-3 min-h-[160px] font-mono text-xs`}
                              value={g.body}
                              onChange={(e) =>
                                setGenState((prev) => ({
                                  ...prev,
                                  [d.id]: { ...g, body: e.target.value },
                                }))
                              }
                            />
                            <div className="mt-3 flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => void saveDraft(d.id)}
                                className={btnSecondary}
                                style={{ ...btnSecondaryStyle, fontSize: 12 }}
                              >
                                Save draft
                              </button>
                              <label className="flex flex-col text-xs text-slate-500">
                                Sent date
                                <input
                                  type="datetime-local"
                                  className={modalInputClass}
                                  value={
                                    markDates[`new-${d.id}`] ??
                                    isoToDatetimeLocalValue(new Date().toISOString())
                                  }
                                  onChange={(e) =>
                                    setMarkDates((prev) => ({ ...prev, [`new-${d.id}`]: e.target.value }))
                                  }
                                />
                              </label>
                              <button
                                type="button"
                                onClick={() => void saveSent(d.id)}
                                className="cursor-pointer rounded-xl border-0 px-3 py-2 text-sm font-semibold text-white"
                                style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                                  boxShadow: "0 4px 12px rgba(16,185,129,0.25)",
                                }}
                              >
                                Save & Mark as Sent
                              </button>
                              <button
                                type="button"
                                onClick={() => printLetter(`Dispute — ${d.creditor}`, g.body)}
                                className={btnSecondary}
                                style={{ ...btnSecondaryStyle, fontSize: 12 }}
                              >
                                View Letter
                              </button>
                              <button
                                type="button"
                                onClick={() => downloadLetterFile(`Dispute-${d.creditor}`, g.body)}
                                className={`${btnSecondary} inline-flex items-center gap-1`}
                                style={{ ...btnSecondaryStyle, fontSize: 12 }}
                              >
                                <Download className="h-3.5 w-3.5" strokeWidth={2} />
                                Download
                              </button>
                            </div>
                          </>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="mt-10">
              <h2 className="mb-4" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 16, color: "#F1F5F9" }}>
                Your letters
              </h2>
              {allLetters.length === 0 ? (
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#64748B" }}>No saved letters yet.</p>
              ) : (
                <div className="space-y-3">
                  {allLetters.map((letter, i) => (
                    <ScrollReveal key={letter.id} delay={i * 0.05}>
                      <div
                        className="card-hover rounded-xl p-4"
                        style={{
                          ...glass,
                          ...(letter.sentAt ? { borderLeft: "3px solid #10B981" } : {}),
                        }}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 14, color: "#F1F5F9" }}>
                              {letter.bureauTarget}
                            </p>
                            <p className="mt-0.5 text-xs" style={{ fontFamily: "'DM Sans', sans-serif", color: "#64748B" }}>
                              {new Date(letter.createdAt).toLocaleString()}
                            </p>
                          </div>
                          {letter.sentAt ? (
                            <span
                              className="rounded-full px-3 py-1 text-[10px] font-semibold"
                              style={{
                                fontFamily: "'DM Sans', sans-serif",
                                background: "rgba(16,185,129,0.12)",
                                color: "#10B981",
                              }}
                            >
                              Sent ✓
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-2 text-xs" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, color: "#94A3B8" }}>
                          {creditorByDisputeId[letter.disputeItemId] ?? "Account"} · {letter.letterType}
                        </p>
                        <p className="mt-1 text-xs" style={{ fontFamily: "'DM Sans', sans-serif", color: "#94A3B8" }}>
                          1 item disputed in this letter
                        </p>
                        {letter.sentAt ? (
                          <p className="mt-2 text-[11px]" style={{ fontFamily: "'DM Sans', sans-serif", color: "#64748B" }}>
                            Sent on {new Date(letter.sentAt).toLocaleDateString()}
                          </p>
                        ) : null}
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              printLetter(
                                `${creditorByDisputeId[letter.disputeItemId] ?? "Letter"}`,
                                letter.letterContent
                              )
                            }
                            className={btnSecondary}
                            style={{ ...btnSecondaryStyle, fontSize: 12 }}
                          >
                            View Letter
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              downloadLetterFile(
                                `${creditorByDisputeId[letter.disputeItemId] ?? "letter"}-${letter.bureauTarget}`,
                                letter.letterContent
                              )
                            }
                            className={`${btnSecondary} inline-flex items-center gap-1`}
                            style={{ ...btnSecondaryStyle, fontSize: 12 }}
                          >
                            <Download className="h-3.5 w-3.5" strokeWidth={2} />
                            Download
                          </button>
                          {!letter.sentAt ? (
                            <>
                              <input
                                type="datetime-local"
                                className={`${modalInputClass} max-w-[200px]`}
                                value={markDates[letter.id] ?? isoToDatetimeLocalValue(new Date().toISOString())}
                                onChange={(e) => setMarkDates((prev) => ({ ...prev, [letter.id]: e.target.value }))}
                              />
                              <button
                                type="button"
                                onClick={() => void markExistingSent(letter)}
                                className="cursor-pointer rounded-xl border-0 px-3 py-2 text-xs font-semibold text-white"
                                style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                                  boxShadow: "0 4px 12px rgba(16,185,129,0.25)",
                                }}
                              >
                                Mark as Sent
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </ScrollReveal>
                  ))}
                </div>
              )}
            </section>

            <section
              className="mt-8 overflow-hidden rounded-2xl"
              style={{
                background: "rgba(14, 165, 233, 0.06)",
                border: "1px solid rgba(14, 165, 233, 0.15)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
              }}
            >
              <button
                type="button"
                onClick={() => setGuideOpen((o) => !o)}
                className="flex w-full items-center justify-between border-0 bg-transparent px-5 py-4 text-left"
                style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 15, color: "#F1F5F9" }}
              >
                📬 Mailing Guide
                <span style={{ color: "#94A3B8" }}>{guideOpen ? "▼" : "▶"}</span>
              </button>
              {guideOpen ? (
                <ul
                  className="space-y-2 border-t px-5 py-4 text-sm"
                  style={{
                    borderColor: "rgba(255,255,255,0.06)",
                    color: "#94A3B8",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  <li>Send via certified mail with return receipt requested.</li>
                  <li>Include a copy of your government-issued photo ID.</li>
                  <li>Include proof of address (utility bill or bank statement).</li>
                  <li className="pt-2 font-medium" style={{ color: "#E2E8F0" }}>
                    Bureau mailing addresses
                  </li>
                  <li>Equifax: P.O. Box 740256, Atlanta, GA 30374-0256</li>
                  <li>Experian: P.O. Box 4500, Allen, TX 75013</li>
                  <li>TransUnion: P.O. Box 2000, Chester, PA 19016</li>
                </ul>
              ) : null}
            </section>
          </>
        )}
      </div>
    </DiyLayout>
  );
};

export default DiyMyLettersPage;
